import type { ChangeEvent } from "react";
import { FIELD_KINDS } from "@eaub/shared";
import type { ContentType, FieldDef, FieldKind } from "@eaub/shared";

export interface FieldErrors {
  key?: string;
  label?: string;
  refTypeId?: string;
}

interface FieldRowEditorProps {
  field: FieldDef;
  index: number;
  total: number;
  contentTypes: ContentType[];
  errors: FieldErrors | undefined;
  onChange: (next: FieldDef) => void;
  onRemove: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
}

function parseIntOrUndefined(value: string): number | undefined {
  if (value === "") return undefined;
  const n = Number(value);
  return Number.isFinite(n) ? n : undefined;
}

export function FieldRowEditor({
  field,
  index,
  total,
  contentTypes,
  errors,
  onChange,
  onRemove,
  onMoveUp,
  onMoveDown,
}: FieldRowEditorProps) {
  const setConfig = (patch: Partial<FieldDef["config"]>) => {
    onChange({ ...field, config: { ...field.config, ...patch } });
  };

  const onKindChange = (e: ChangeEvent<HTMLSelectElement>) => {
    // Reset config when the kind changes so stale config keys are dropped.
    onChange({ ...field, kind: e.target.value as FieldKind, config: {} });
  };

  return (
    <div className="field-row">
      <div className="field-row__drag">
        <span className="field-row__index">#{index + 1}</span>
        <div className="field-row__move">
          <button
            type="button"
            className="btn btn--ghost btn--xs"
            onClick={onMoveUp}
            disabled={index === 0}
            aria-label="Move field up"
          >
            ↑
          </button>
          <button
            type="button"
            className="btn btn--ghost btn--xs"
            onClick={onMoveDown}
            disabled={index === total - 1}
            aria-label="Move field down"
          >
            ↓
          </button>
        </div>
      </div>

      <div className="field-row__grid">
        <div className="form-field">
          <label className="form-field__label" htmlFor={`field-key-${index}`}>
            Key
          </label>
          <input
            id={`field-key-${index}`}
            className={errors?.key ? "input input--error" : "input"}
            value={field.key}
            placeholder="snake_case"
            onChange={(e) => onChange({ ...field, key: e.target.value })}
          />
          {errors?.key ? <span className="form-field__error">{errors.key}</span> : null}
        </div>

        <div className="form-field">
          <label className="form-field__label" htmlFor={`field-label-${index}`}>
            Label
          </label>
          <input
            id={`field-label-${index}`}
            className={errors?.label ? "input input--error" : "input"}
            value={field.label}
            onChange={(e) => onChange({ ...field, label: e.target.value })}
          />
          {errors?.label ? <span className="form-field__error">{errors.label}</span> : null}
        </div>

        <div className="form-field">
          <label className="form-field__label" htmlFor={`field-kind-${index}`}>
            Kind
          </label>
          <select
            id={`field-kind-${index}`}
            className="input"
            value={field.kind}
            onChange={onKindChange}
          >
            {FIELD_KINDS.map((kind) => (
              <option key={kind} value={kind}>
                {kind}
              </option>
            ))}
          </select>
        </div>

        <div className="form-field form-field--check">
          <label className="checkbox-row">
            <input
              type="checkbox"
              checked={field.required}
              onChange={(e) => onChange({ ...field, required: e.target.checked })}
            />
            <span>Required</span>
          </label>
        </div>
      </div>

      <div className="field-row__config">
        {(field.kind === "text" || field.kind === "richtext") && (
          <>
            <div className="form-field form-field--sm">
              <label className="form-field__label">Min length</label>
              <input
                type="number"
                className="input"
                min={0}
                value={field.config.minLength ?? ""}
                onChange={(e) => setConfig({ minLength: parseIntOrUndefined(e.target.value) })}
              />
            </div>
            <div className="form-field form-field--sm">
              <label className="form-field__label">Max length</label>
              <input
                type="number"
                className="input"
                min={1}
                value={field.config.maxLength ?? ""}
                onChange={(e) => setConfig({ maxLength: parseIntOrUndefined(e.target.value) })}
              />
            </div>
          </>
        )}

        {field.kind === "number" && (
          <>
            <div className="form-field form-field--sm">
              <label className="form-field__label">Min</label>
              <input
                type="number"
                className="input"
                value={field.config.min ?? ""}
                onChange={(e) => setConfig({ min: parseIntOrUndefined(e.target.value) })}
              />
            </div>
            <div className="form-field form-field--sm">
              <label className="form-field__label">Max</label>
              <input
                type="number"
                className="input"
                value={field.config.max ?? ""}
                onChange={(e) => setConfig({ max: parseIntOrUndefined(e.target.value) })}
              />
            </div>
            <div className="form-field form-field--check">
              <label className="checkbox-row">
                <input
                  type="checkbox"
                  checked={field.config.integer ?? false}
                  onChange={(e) => setConfig({ integer: e.target.checked })}
                />
                <span>Integer only</span>
              </label>
            </div>
          </>
        )}

        {(field.kind === "media" || field.kind === "reference") && (
          <div className="form-field form-field--check">
            <label className="checkbox-row">
              <input
                type="checkbox"
                checked={field.config.multiple ?? false}
                onChange={(e) => setConfig({ multiple: e.target.checked })}
              />
              <span>Allow multiple</span>
            </label>
          </div>
        )}

        {field.kind === "reference" && (
          <div className="form-field">
            <label className="form-field__label" htmlFor={`field-ref-${index}`}>
              References type
            </label>
            <select
              id={`field-ref-${index}`}
              className={errors?.refTypeId ? "input input--error" : "input"}
              value={field.config.refTypeId ?? ""}
              onChange={(e) =>
                setConfig({ refTypeId: e.target.value === "" ? undefined : e.target.value })
              }
            >
              <option value="">— choose a content type —</option>
              {contentTypes.map((ct) => (
                <option key={ct.id} value={ct.id}>
                  {ct.name}
                </option>
              ))}
            </select>
            {errors?.refTypeId ? (
              <span className="form-field__error">{errors.refTypeId}</span>
            ) : null}
          </div>
        )}
      </div>

      <div className="field-row__footer">
        <button type="button" className="btn btn--danger btn--sm" onClick={onRemove}>
          Remove field
        </button>
      </div>
    </div>
  );
}
